import { LinkListEditor } from "../../components/LinkListEditor";
import { normalizeLinks, linksAreValid } from "../../utils/linkLists";
import React, { useState, useEffect } from "react";
import { Box, Stack, Typography } from "@mui/material";
import {
  Settings,
  User,
  Calendar,
  AlertTriangle,
  Link as LinkIcon,
  CalendarPlus,
} from "lucide-react";

import { Avatar } from "../../components/Avatar";
import { UnifiedModal } from "../../components/UnifiedModal";
import { Button } from "../../components/Button";
import { CustomTextField } from "../../components/CustomTextField";
import { Label } from "../../components/Label";
import { CustomSelector } from "../../components/CustomSelector";
import { useTheme } from "../../theme/useTheme";
import { patchWithRefresh } from "../../api-client";

/**
 * Modal para editar tareas del setup técnico
 */
export const EditTaskModal = ({
  open,
  onClose,
  task,
  consultants = [],
  onTaskUpdated,
  onSuccessPopup,
  onErrorPopup,
  isFromKanban = false,
  isPhaseEdit = false,
  hideTaskInfo = false,
  onSubmitOverride = null,
}) => {
  const { theme, colors, neutrals } = useTheme();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    assignedTo: "",
    deadline: "",
    documentationUrls: [],
  });
  const [initialFormData, setInitialFormData] = useState({
    assignedTo: "",
    deadline: "",
    documentationUrls: [],
  });
  const [validationErrors, setValidationErrors] = useState({});

  const INVALID_URL_MESSAGE = "Por favor, introduce una url válida (Ejemplo: https://miweb.com)";

  const backendHasUrlError = (data) => {
    const containsUrlError = (value) => {
      if (typeof value === "string") {
        return value.toLowerCase().includes("valid url");
      }
      if (Array.isArray(value)) {
        return value.some((item) => containsUrlError(item));
      }
      if (value && typeof value === "object") {
        return Object.values(value).some((item) => containsUrlError(item));
      }
      return false;
    };

    return containsUrlError(data);
  };

  const resolveAssignedToName = (assignedToValue) => {
    if (
      assignedToValue === null ||
      assignedToValue === "" ||
      assignedToValue === undefined
    ) {
      return "Sin asignar";
    }

    const parsedId = Number(assignedToValue);
    if (Number.isNaN(parsedId)) {
      return "Sin asignar";
    }

    const assignedConsultant = consultants.find(
      (consultant) => consultant.id === parsedId
    );
    return assignedConsultant?.full_name || "Sin asignar";
  };

  // Inicializar formulario cuando se abre el modal
  useEffect(() => {
    if (task && open) {
      let newFormData;

      if (isPhaseEdit) {
        // Para edición de fase, determinar valores comunes
        const allTasks = task.tasks || [];
        const tasks = allTasks.filter((t) => t.status !== "no_necesario");
        const allAssignedTo = tasks.map((t) => t.assigned_to);
        const uniqueAssignedTo = [...new Set(allAssignedTo)];
        const commonAssignedTo =
          uniqueAssignedTo.length === 1 ? uniqueAssignedTo[0] : "";

        const allDeadlines = tasks.map((t) => t.deadline);
        const uniqueDeadlines = [...new Set(allDeadlines)];
        const commonDeadline =
          uniqueDeadlines.length === 1 ? uniqueDeadlines[0]?.split("T")[0] : "";

        newFormData = {
          assignedTo:
            commonAssignedTo === "" ? "independent" : commonAssignedTo || "",
          deadline:
            commonDeadline === "" ? "independent" : commonDeadline || "",
          documentationUrls: [], // Not applicable for phase edit
        };
      } else {
        // Para edición de tarea individual
        newFormData = {
          assignedTo: task.assigned_to || task.assignee || "",
          deadline:
            (task.deadline ? task.deadline.split("T")[0] : "") ||
            (task.dueDate ? task.dueDate.split("T")[0] : "") ||
            "",
          documentationUrls: task.documentation_urls || [],
        };
      }

      setFormData(newFormData);
      setInitialFormData(newFormData);
    }
  }, [task, open, isPhaseEdit]);
  // Opciones de consultores y admins ordenados: primero admins, luego consultores
  const consultantOptions = consultants
    .map((consultant) => {
      // Mapear roles a texto legible
      const roleLabels = {
        agency_admin: "Admin",
        consultant: "Consultor",
        superadmin: "Superadmin",
      };
      const roleLabel = roleLabels[consultant.role] || consultant.role || "";

      return {
        value: consultant.id,
        label: `${consultant.full_name || "Desconocido"}${roleLabel ? ` (${roleLabel})` : ""}`,
        icon: (
          <Avatar
            size="sm"
            name={consultant.full_name || consultant.email}
            src={consultant.profile_image || undefined}
          />
        ),
        role: consultant.role, // Mantener el rol para ordenar
      };
    })
    .sort((a, b) => {
      // Definir el orden de prioridad de roles
      const roleOrder = {
        agency_admin: 1,
        consultant: 2,
        superadmin: 3,
      };

      const orderA = roleOrder[a.role] || 999;
      const orderB = roleOrder[b.role] || 999;

      // Si tienen el mismo rol, ordenar alfabéticamente por nombre
      if (orderA === orderB) {
        return a.label.localeCompare(b.label);
      }

      return orderA - orderB;
    });

  // Función de validación de URL

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!task?.id && !onSubmitOverride) {
      return;
    }

    // Validar URL antes de enviar
    const urlError = !linksAreValid(formData.documentationUrls) ? INVALID_URL_MESSAGE : null;
    if (urlError) {
      setValidationErrors({
        documentationUrls: urlError
      });
      return;
    }

    // Limpiar errores de validación
    setValidationErrors({});

    // Verificar si los valores han cambiado
    const hasChanged =
      formData.assignedTo !== initialFormData.assignedTo ||
      formData.deadline !== initialFormData.deadline ||
      JSON.stringify(formData.documentationUrls) !== JSON.stringify(initialFormData.documentationUrls);

    if (!hasChanged) {
      // Si no han cambiado, solo cerrar el modal
      onClose();
      return;
    }

    try {
      setIsSubmitting(true);

      const parsedAssignedTo = formData.assignedTo
        ? parseInt(formData.assignedTo, 10)
        : null;

      if (onSubmitOverride) {
        await onSubmitOverride({
          task,
          values: {
            assigned_to: parsedAssignedTo,
            deadline: formData.deadline || null,
            documentation_urls: normalizeLinks(formData.documentationUrls),
          },
          rawForm: formData,
        });
        onClose();
        return;
      }

      if (isPhaseEdit) {
        // Para edición de fase, actualizar todas las tareas
        const tasksInPhase = task.tasks || [];
        const tasksToUpdate = tasksInPhase
          .map((taskItem) => {
            const updateData = {};

            if (
              formData.assignedTo !== "" &&
              formData.assignedTo !== initialFormData.assignedTo
            ) {
              updateData.assigned_to = parsedAssignedTo;
            }
            if (
              formData.deadline !== "" &&
              formData.deadline !== initialFormData.deadline
            ) {
              updateData.deadline = formData.deadline || null;
            }

            if (Object.keys(updateData).length === 0) {
              return null;
            }

            return { taskItem, updateData };
          })
          .filter(Boolean);

        await Promise.all(
          tasksToUpdate.map(({ taskItem, updateData }) =>
            patchWithRefresh(`projects/tasks/${taskItem.id}/`, updateData)
          )
        );

        if (onTaskUpdated && tasksToUpdate.length > 0) {
          const updatedTasksPayload = tasksToUpdate.map(
            ({ taskItem, updateData }) => {
              const payload = { id: taskItem.id };

              if (
                Object.prototype.hasOwnProperty.call(updateData, "assigned_to")
              ) {
                payload.assigned_to = updateData.assigned_to;
                payload.assigned_to_name = resolveAssignedToName(
                  updateData.assigned_to
                );
              }

              if (
                Object.prototype.hasOwnProperty.call(updateData, "deadline")
              ) {
                payload.deadline = updateData.deadline;
                payload.dueDate = updateData.deadline;
              }

              return payload;
            }
          );

          onTaskUpdated(updatedTasksPayload);
        }

        // Mostrar popup de éxito
        if (onSuccessPopup) {
          onSuccessPopup("Fase actualizada correctamente");
        }
      } else {
        // Para edición de tarea individual
        // Preparar datos para el PATCH
        const updateData = {
          assigned_to: parsedAssignedTo,
          deadline: formData.deadline || null,
          documentation_urls: normalizeLinks(formData.documentationUrls),
        };

        // Hacer llamada PATCH al backend
        await patchWithRefresh(`projects/tasks/${task.id}/`, updateData);

        // Si no es desde kanban, hacer refresh y mostrar popup
        if (!isFromKanban) {
          // Mostrar popup de éxito
          if (onSuccessPopup) {
            onSuccessPopup("Tarea actualizada correctamente");
          }
        }

        // Notificar al componente padre que la tarea fue actualizada
        if (onTaskUpdated) {
          onTaskUpdated({
            ...task,
            assigned_to: parsedAssignedTo,
            assigned_to_name: resolveAssignedToName(parsedAssignedTo),
            deadline: formData.deadline,
            dueDate: formData.deadline,
            documentation_urls: normalizeLinks(formData.documentationUrls),
          });
        }
      }

      // Cerrar modal
      onClose();
    } catch (error) {
      console.error("Error al actualizar:", error);
      if (onSubmitOverride && error?.message) {
        if (onErrorPopup) {
          onErrorPopup(error.message);
        }
      } else {
        const backendData = error.response?.data;
        if (backendHasUrlError(backendData)) {
          setValidationErrors({ documentationUrls: INVALID_URL_MESSAGE });
          if (!isFromKanban && onErrorPopup) {
            onErrorPopup(INVALID_URL_MESSAGE);
          }
        } else {
          // Si no es desde kanban, mostrar popup de error
          if (!isFromKanban) {
            if (onErrorPopup) {
              onErrorPopup(
                backendData?.detail ||
                  backendData?.message ||
                  (isPhaseEdit
                    ? "Error al actualizar la fase. Por favor, intenta de nuevo."
                    : "Error al actualizar la tarea. Por favor, intenta de nuevo.")
              );
            }
          }
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  if (!task && !onSubmitOverride) return null;
  const calendarLink = !isPhaseEdit ? task?.google_calendar_link : null;

  const handleOpenCalendarEvent = () => {
    if (!calendarLink) return;
    if (typeof window !== "undefined") {
      window.open(calendarLink, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <UnifiedModal
      open={open}
      onClose={handleClose}
      title={isPhaseEdit ? "Editar Fase" : "Editar Tarea"}
      maxWidth="sm"
    >
      <Box sx={{ pt: 0.5, pb: 2, px: 2 }}>
        <Stack spacing={2}>
          {/* Información de la tarea o fase */}
          {!hideTaskInfo && task && (
            <Box>
              <Stack
                direction="row"
                alignItems="center"
                spacing={2}
                sx={{ mb: 1 }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "8px",
                    backgroundColor: `${colors.primary.main}15`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Settings size={20} color={colors.primary.main} />
                </Box>
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      fontSize: "1rem",
                      color: theme.palette.text.primary,
                    }}
                  >
                    {isPhaseEdit ? task.title : task.title || task.name}
                  </Typography>
                  {isPhaseEdit ? (
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontSize: "0.875rem",
                        mt: 0.5,
                      }}
                    >
                      Los cambios realizados se aplicarán a todas las tareas de
                      esta fase.
                    </Typography>
                  ) : (
                    task?.description && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: theme.palette.text.secondary,
                          fontSize: "0.875rem",
                          mt: 0.5,
                        }}
                      >
                        {task.description}
                      </Typography>
                    )
                  )}
                </Box>
              </Stack>
            </Box>
          )}

          {/* Formulario */}
          <form onSubmit={handleSubmit}>
            <Stack spacing={3}>
              {/* Asignado a */}
              <Box>
                <Label
                  sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}
                >
                  <User size={16} color={theme.palette.text.secondary} />
                  Asignado a
                </Label>
                <CustomSelector
                  value={formData.assignedTo}
                  onChange={(value) =>
                    setFormData({ ...formData, assignedTo: value })
                  }
                  options={
                    isPhaseEdit
                      ? consultantOptions
                      : [
                        { value: "", label: "Sin asignar" },
                        ...consultantOptions,
                      ]
                  }
                  placeholder={
                    isPhaseEdit && formData.assignedTo === "independent"
                      ? "Gestión actual independiente en cada tarea"
                      : "Seleccionar miembro"
                  }
                />
              </Box>

              {/* Fecha límite */}
              <Box>
                <Label
                  sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}
                >
                  <Calendar size={16} color={theme.palette.text.secondary} />
                  Fecha límite
                </Label>
                <Stack direction="row" spacing={2} alignItems="flex-end">
                  <CustomTextField
                    type="date"
                    value={formData.deadline}
                    onChange={(e) =>
                      setFormData({ ...formData, deadline: e.target.value })
                    }
                    placeholder={
                      isPhaseEdit && formData.deadline === "independent"
                        ? "Gestión actual independiente en cada tarea"
                        : undefined
                    }
                    sx={{
                      "& input": {
                        cursor: "pointer",
                      },
                      flex: 1,
                    }}
                  />
                  {calendarLink && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleOpenCalendarEvent}
                      sx={{
                        justifyContent: "flex-start",
                        gap: 1,
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                      }}
                    >
                      <CalendarPlus size={16} />
                      Añadir a Google Calendar
                    </Button>
                  )}
                </Stack>
              </Box>

              {/* Enlaces de documentación - Solo para edición individual */}
              {!isPhaseEdit && (
                <Box>
                  <Label
                    sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <LinkIcon
                      size={16}
                      color={theme.palette.text.secondary}
                    />
                    Enlaces de documentación
                  </Label>

                  <LinkListEditor value={formData.documentationUrls} onChange={(documentationUrls) => setFormData((prev) => ({ ...prev, documentationUrls }))} disabled={isSubmitting} />
                  {validationErrors.documentationUrls && <Typography color="error" variant="caption">{validationErrors.documentationUrls}</Typography>}
                </Box>
              )}

              {/* Botones */}
              <Stack direction="row" spacing={2} sx={{ pt: 2 }}>
                <Button
                  variant="outline"
                  onClick={handleClose}
                  disabled={isSubmitting}
                  sx={{ flex: 1 }}
                >
                  Cancelar
                </Button>
                <Button type="submit" disabled={isSubmitting} sx={{ flex: 1 }}>
                  {isSubmitting ? "Guardando..." : "Confirmar"}
                </Button>
              </Stack>
            </Stack>
          </form>
        </Stack>
      </Box>
    </UnifiedModal>
  );
};
